import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Constellation, type ConstellationNode, type ConstellationViewStats } from '@diwata/ironvale'
import { useUI } from '@/store/ui'
import { useSelection } from '@/store/selection'
import { useGraphLens, type Lens } from '@/store/graphLens'
import { useGraphFocus } from '@/store/graphFocus'
import {
  AE,
  ACTIVITY_DOT,
  domainActivity,
  domainColorMap,
  domainStats,
  EGO,
  GLOW,
  posPatch,
  readNodeColor,
  readNodePos,
  scopeToDomains,
  type Group,
} from '@/lib/graphColors'
import { syntheticGraph } from '@/lib/synthetic'
import { GraphAPI, type GraphNode } from '@/api/graph'
import { DomainsAPI } from '@/api/domains'
import { ConstellationSearch } from '@/components/ConstellationSearch'
import { GraphEditor } from '@/components/GraphEditor'
import { HelpHint } from '@/components/HelpHint'
import { useOnboardingReentry } from '@/components/Onboarding'
import { useIsMobile } from '@/lib/useIsMobile'
import './constellation.css'

// GraphView — the Diwa constellation. Since the ironvale Constellation primitive landed, this
// file is a THIN BINDING: it fetches the knowledge graph, maps Diwa's entities/relations onto the
// primitive's generic node/edge model, and hands Diwa's semantics in through props/callbacks.
//
// The primitive owns everything that is domain-agnostic: the force layout, the rings by hop
// distance, the camera (zoom/pan/pinch/fit), drag-to-pin, hover-neighborhood, labels, the galaxy
// crossfade, timelapse, and containment. This wrapper owns everything that is Diwa: the domain
// tint, the lens (what size/brightness MEAN), the entity peek/dossier selection, the pin PATCH,
// the domain-scoping chips, the guided empty sky, and the keyboard SkySearch.

/** Rings are degrees of separation; the outer ring holds anything 4+ introductions away. */
const MAX_HOP = 4

function groupOf(n: GraphNode): Group {
  if (n.self) return 'bridge'
  if (n.type === 'event') return 'signal'
  if (n.world === 'fund') return 'fund'
  if (n.world === 'fi') return 'fi'
  if (n.world === 'vc') return 'vc'
  if (n.world === 'markets') return 'markets'
  if (n.world === 'media') return 'media'
  if (n.world === 'studio') return 'studio'
  return 'other'
}

// Lenses remap what size and brightness MEAN — one channel at a time stays legible.
// STRUCTURE (default): size = connectedness, brightness = score rank.
// HEAT: size + brightness = fresh-signal activity; quiet nodes recede.
// FRESHNESS and CONFIDENCE keep structural sizing and speak through brightness alone.
function radius(n: GraphNode, degree: number, lens: Lens): number {
  if (n.self) return 26
  if (lens === 'heat') {
    const heat = n.heat ?? 0
    return heat === 0 ? 6 : 10 + Math.min(14, heat * 5)
  }
  if (n.type === 'event') return 8
  return 9 + Math.min(13, Math.sqrt(degree) * 5)
}

/** Most recent touch: entity update or enrichment, whichever is later. */
function lastTouched(n: GraphNode): number {
  const updated = n.updated_at ? Date.parse(n.updated_at) : 0
  const enriched = n.enriched_at ? Date.parse(n.enriched_at) : 0
  return Math.max(updated, enriched)
}

export function GraphView() {
  // Phones get the constellation's search posture — no sim, no galaxy, straight to
  // the Dossier. The sky needs a bigger window.
  const isMobile = useIsMobile()
  if (isMobile) return <ConstellationSearch />
  return <GraphViewDesktop />
}

function GraphViewDesktop() {
  const { diwaUrl } = useUI()
  const { select } = useSelection()
  const api = new GraphAPI(diwaUrl)
  const navigate = useNavigate()
  const replayWelcome = useOnboardingReentry()
  // Guided empty state: "Add a star" folds the control plane's Add form out right here.
  const [showAdd, setShowAdd] = useState(false)
  // Multi-domain scope: an empty set means "all domains".
  const [activeDomains, setActiveDomains] = useState<Set<string>>(new Set())
  const toggleDomain = useCallback((slug: string) => {
    setActiveDomains((prev) => {
      const next = new Set(prev)
      if (next.has(slug)) next.delete(slug)
      else next.add(slug)
      return next
    })
  }, [])
  const { data: domainsData } = useQuery({ queryKey: ['domains'], queryFn: () => new DomainsAPI(diwaUrl).list() })
  // Dev-only scale harness: ?synthetic=2000 swaps the API payload for a generated sky.
  const syntheticN = import.meta.env.DEV
    ? Number(new URLSearchParams(window.location.search).get('synthetic')) || 0
    : 0
  const { data: fetched, isLoading, isError, error } = useQuery({
    queryKey: ['graph-network', 'all'],
    queryFn: () => api.network('all'),
    enabled: syntheticN === 0,
  })
  const data = useMemo(
    () => (syntheticN > 0 ? syntheticGraph(syntheticN) : fetched),
    [syntheticN, fetched],
  )

  // Domain scoping that KEEPS cross-domain neighbors — the "one connected world" view.
  const view = useMemo(() => (data ? scopeToDomains(data, activeDomains) : data), [data, activeDomains])

  const emptySky = !!data && data.nodes.every((n) => n.self)
  const filteredEmpty = !emptySky && !!view && view.nodes.every((n) => n.self)

  const allStats = useMemo(() => domainStats((data?.nodes ?? []).filter((n) => !n.self)), [data])
  const egoDomain = useMemo(() => data?.nodes.find((n) => n.self)?.domain ?? null, [data])
  const pickerDomains = useMemo(() => {
    const declared = domainsData?.domains ?? []
    const seen = new Set(declared.map((d) => d.slug))
    const extra = [...allStats.keys()]
      .filter((slug) => !seen.has(slug))
      .sort()
      .map((slug) => ({ id: slug, slug, name: slug === egoDomain ? 'You / ego' : slug, status: 'active' as string }))
    return [
      ...declared.map((d) => ({ id: d.id, slug: d.slug, name: d.name, status: d.status })),
      ...extra,
    ]
  }, [domainsData, allStats, egoDomain])

  // A graph you can render whole today is a hairball at second-brain scale. The default view is a
  // QUESTION — "who is within two introductions of me?" — not a picture of the whole database.
  const [reach, setReach] = useState<number>(2)
  const lens = useGraphLens((s) => s.lens)
  const focusId = useGraphFocus((st) => st.focusId)
  const setFocus = useGraphFocus((st) => st.setFocus)

  const egoId = useMemo(() => view?.nodes.find((n) => n.self)?.id ?? null, [view])

  // Degree over the whole connected view — "connectedness" is a property of the graph, not of
  // whatever slice the reach selector shows.
  const degree = useMemo(() => {
    const d = new Map<string, number>()
    for (const e of view?.edges ?? []) {
      d.set(e.source, (d.get(e.source) ?? 0) + 1)
      d.set(e.target, (d.get(e.target) ?? 0) + 1)
    }
    return d
  }, [view])

  // Brightness as a percentile within the view, so whatever spread exists is spread across the
  // full contrast range. Absolute mappings waste a third of the range on values nothing has.
  const brightness = useMemo(() => {
    const scored = (view?.nodes ?? []).filter((n) => !n.self && n.type !== 'event')
    const rank = new Map<string, number>()
    if (lens === 'confidence') {
      for (const n of scored) rank.set(n.id, typeof n.confidence === 'number' ? Math.max(0.15, n.confidence) : 0.1)
      return rank
    }
    const metric = (n: GraphNode) =>
      lens === 'heat' ? n.heat ?? 0 : lens === 'freshness' ? lastTouched(n) : n.score ?? 0
    const sorted = [...scored].sort((a, b) => metric(a) - metric(b))
    sorted.forEach((n, i) => rank.set(n.id, sorted.length > 1 ? i / (sorted.length - 1) : 1))
    if (lens === 'heat') {
      for (const n of scored) if (!n.heat) rank.set(n.id, 0.12)
    }
    return rank
  }, [view, lens])

  // One hue per domain, over the SAME slug set the View-tab legend uses (declared regions ∪ node
  // domains), so a domain wears one colour in the sky, the hover card, and the legend alike.
  const domainColor = useMemo(
    () =>
      domainColorMap([
        ...(domainsData?.domains ?? []).map((d) => d.slug),
        ...(data?.nodes ?? []).map((n) => n.domain),
      ]),
    [domainsData, data],
  )
  const getGroupColor = useCallback((slug: string) => domainColor.get(slug), [domainColor])

  // ── The seam: Diwa entities/relations → the primitive's generic node/edge model ──────────────
  const nodesById = useMemo(() => new Map((view?.nodes ?? []).map((n) => [n.id, n])), [view])
  const cnodes = useMemo<ConstellationNode[]>(
    () =>
      (view?.nodes ?? []).map((n) => {
        const override = readNodeColor(n.attributes)
        return {
          id: n.id,
          label: n.name,
          group: n.domain,
          // A user override colours the whole star; otherwise the ego is crimson (via theme) and
          // every other star wears its world-hued halo over a parchment core (theme default).
          color: override ?? undefined,
          glow: override ?? (n.self ? EGO.halo : GLOW[groupOf(n)]),
          weight: radius(n, degree.get(n.id) ?? 0, lens),
          brightness: n.self ? 1 : brightness.get(n.id) ?? 0.5,
          pinnedPos: readNodePos(n.attributes),
          t: n.created_at ? Date.parse(n.created_at) : undefined,
          meta: n.type,
        }
      }),
    [view, degree, lens, brightness],
  )
  const cedges = useMemo(
    () =>
      (view?.edges ?? []).map((e) => ({
        from: e.source,
        to: e.target,
        label: e.type,
        kind: e.provenance ?? 'confirmed',
        t: e.created_at ? Date.parse(e.created_at) : undefined,
      })),
    [view],
  )

  // Selection → the entity peek / Inspect rail. The primitive hands back a generic node; we look
  // the original Diwa entity back up to carry its full shape into the selection store.
  const onNodeClick = useCallback(
    (node: ConstellationNode) => {
      const n = nodesById.get(node.id)
      if (n) select({ id: n.id, name: n.name, type: n.type, world: n.world, score: n.score, domain: n.domain, attributes: n.attributes })
    },
    [nodesById, select],
  )
  // Drag-end = pin. "Drag to arrange" is a promise: the star stays where dropped, this session
  // (the primitive holds it) and the next (PATCH attributes.pos). Fire-and-forget; a lost write
  // costs one arrangement, never the interaction. Synthetic skies have no server to write to.
  const onNodePin = useCallback(
    (id: string, pos: { x: number; y: number }) => {
      if (syntheticN > 0) return
      new GraphAPI(diwaUrl).patchEntityAttributes(id, posPatch(pos.x, pos.y)).catch(() => {})
    },
    [diwaUrl, syntheticN],
  )

  const [stats, setStats] = useState<ConstellationViewStats | null>(null)
  const onViewStats = useCallback((s: ConstellationViewStats) => setStats(s), [])

  const theme = useMemo(
    () => ({
      ring: AE.gold,
      star: AE.parchment,
      starHalo: GLOW.other,
      focusCore: EGO.core,
      focusHalo: EGO.halo,
      label: AE.parchment,
      sector: AE.ash,
    }),
    [],
  )

  return (
    <div>
      <div className="dw-page-header">
        <div>
          <div className="dw-page-title">Constellation</div>
          {!emptySky && stats && (
            <div className="dw-page-subtitle">
              {stats.visible} entities · {stats.relations} relations
              {stats.hidden > 0 && (
                <>
                  {' · '}
                  <HelpHint
                    gloss={`Reach hides anything more than ${reach} introductions from the center — widen it to "everything" to see the whole sky.`}
                  >
                    {stats.hidden} beyond {reach} hops hidden
                  </HelpHint>
                </>
              )}
              {' · drag a star to pin it — arrangements keep'}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginRight: 12 }}>
          {!emptySky && (
            <SkySearch
              nodes={data?.nodes ?? []}
              onPick={(n) =>
                select({ id: n.id, name: n.name, type: n.type, world: n.world, score: n.score, domain: n.domain, attributes: n.attributes ?? null })
              }
            />
          )}
          <label style={{ fontSize: 'calc(11px * var(--ae-text-scale, 1))', color: 'var(--ae-color-text-muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <HelpHint gloss="How far the sky reaches from its center, in hops — introductions. 2 hops = your stars and theirs.">
              reach
            </HelpHint>
            <select className="dw-input" style={{ width: 110 }} value={reach} onChange={(e) => setReach(Number(e.target.value))}>
              <option value={2}>2 hops</option>
              <option value={3}>3 hops</option>
              <option value={MAX_HOP}>everything</option>
            </select>
          </label>
        </div>
      </div>

      {/* Multi-domain picker: chips union (no single-select dropdown). Each chip carries the
          region's live entity count and an activity dot. "all" clears the selection. */}
      <div
        className="dw-chip-row"
        role="group"
        aria-label="Filter constellation by domain"
        style={{ marginBottom: 12, flexWrap: 'wrap', rowGap: 6 }}
      >
        <button
          type="button"
          className={`dw-chip dw-chip--toggle${activeDomains.size === 0 ? ' dw-chip--on' : ''}`}
          aria-pressed={activeDomains.size === 0}
          onClick={() => setActiveDomains(new Set())}
        >
          all domains
        </button>
        {pickerDomains.map((d) => {
          const stat = allStats.get(d.slug)
          const count = stat?.count ?? 0
          const on = activeDomains.has(d.slug)
          const activity = domainActivity({
            count,
            archived: d.status === 'archived',
            lastTouched: stat?.lastTouched ?? null,
          })
          return (
            <button
              key={d.id}
              type="button"
              className={`dw-chip dw-chip--toggle${on ? ' dw-chip--on' : ''}`}
              aria-pressed={on}
              title={`${count} ${count === 1 ? 'entity' : 'entities'} · ${activity === 'active' ? 'a spring ran recently' : 'quiet'}`}
              onClick={() => toggleDomain(d.slug)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <span
                aria-hidden
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: ACTIVITY_DOT[activity],
                  boxShadow: activity === 'active' ? `0 0 4px ${ACTIVITY_DOT.active}` : undefined,
                  flex: '0 0 auto',
                }}
              />
              {d.name}
              <span style={{ opacity: 0.6, fontVariantNumeric: 'tabular-nums' }}>{count}</span>
            </button>
          )
        })}
      </div>

      <div className="dw-graph-shell" style={{ height: 'min(760px, calc(100vh - 220px))', minHeight: 320 }}>
        {isLoading && <p className="dw-empty">Loading…</p>}
        {isError && <p className="dw-error">Couldn’t load graph: {(error as Error).message}</p>}

        {emptySky && !isLoading && !isError && (
          <div className="dw-graph-sky dw-graph-sky--empty">
            <div className="dw-sky-empty">
              <div className="dw-sky-empty-panel">
                <div className="dw-sky-empty-title">Your sky is empty — for now</div>
                <p className="dw-sky-empty-copy">
                  The constellation draws everything Diwa knows — people, companies, events — as stars
                  around you. Create a domain to give your world its first region, or add a single
                  star by hand and watch the sky begin.
                </p>
                <div className="dw-sky-empty-actions">
                  <button type="button" className="dw-btn" onClick={() => navigate('/domains?create=1')}>
                    Create a domain
                  </button>
                  <button
                    type="button"
                    className="dw-btn"
                    aria-expanded={showAdd}
                    onClick={() => setShowAdd((v) => !v)}
                  >
                    Add a star
                  </button>
                  <button type="button" className="dw-inline-link" onClick={replayWelcome}>
                    ✦ Replay the welcome
                  </button>
                </div>
                {showAdd && (
                  <div className="dw-sky-empty-editor">
                    <GraphEditor />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {filteredEmpty && !isLoading && (
          <p className="dw-empty">No stars in the selected domains yet — pick another region above.</p>
        )}

        {!emptySky && !filteredEmpty && !isLoading && !isError && view && view.nodes.length > 0 && (
          <Constellation
            nodes={cnodes}
            edges={cedges}
            egoId={egoId}
            focusId={focusId}
            reach={reach}
            maxHop={MAX_HOP}
            theme={theme}
            getGroupColor={getGroupColor}
            onNodeClick={onNodeClick}
            onNodePin={onNodePin}
            onResetFocus={() => setFocus(null)}
            onViewStats={onViewStats}
          />
        )}
      </div>
    </div>
  )
}

/**
 * The desktop keyboard path into the sky. The canvas itself is pointer-first; this combobox is the
 * accessible alternative: Tab reaches it, typing filters the WHOLE graph, arrows move the
 * selection, Enter selects the star — which opens its Dossier in the control plane. Escape clears.
 */
function SkySearch({ nodes, onPick }: { nodes: GraphNode[]; onPick: (n: GraphNode) => void }) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [idx, setIdx] = useState(0)
  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return []
    return nodes.filter((n) => !n.self && n.name.toLowerCase().includes(needle)).slice(0, 8)
  }, [nodes, q])
  const show = open && q.trim().length > 0
  const active = matches[Math.min(idx, matches.length - 1)]
  return (
    <div className="dw-sky-search">
      <input
        className="dw-input"
        style={{ width: 180 }}
        type="text"
        role="combobox"
        aria-expanded={show}
        aria-controls="dw-sky-search-list"
        aria-activedescendant={show && active ? `dw-sky-opt-${active.id}` : undefined}
        aria-label="Search stars by name"
        placeholder="Find a star…"
        value={q}
        onChange={(e) => {
          setQ(e.target.value)
          setOpen(true)
          setIdx(0)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setIdx((i) => Math.min(i + 1, matches.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setIdx((i) => Math.max(i - 1, 0))
          } else if (e.key === 'Enter' && active) {
            e.preventDefault()
            onPick(active)
            setOpen(false)
          } else if (e.key === 'Escape') {
            setQ('')
            setOpen(false)
          }
        }}
      />
      {show && (
        <div className="dw-sky-search-list" id="dw-sky-search-list" role="listbox" aria-label="Matching stars">
          {matches.map((n, i) => (
            <button
              key={n.id}
              id={`dw-sky-opt-${n.id}`}
              type="button"
              role="option"
              aria-selected={i === idx}
              className="dw-sky-search-opt"
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setIdx(i)}
              onClick={() => {
                onPick(n)
                setOpen(false)
              }}
            >
              <span>{n.name}</span>
              <span className="dw-sky-search-opt-meta">
                {n.type} · {n.domain}
              </span>
            </button>
          ))}
          {matches.length === 0 && <div className="dw-sky-search-none">No star answers to that name.</div>}
        </div>
      )}
    </div>
  )
}
